import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';

import Avatar from '@components/Avatar/Avatar';
import { heroColorForIndex } from '@components/MyTasks/myTasksGrouping';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Task } from '@graphql/generated';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { localDateFromUTCDate } from '@utils/formatters/date';

type HeroCarouselProps = {
  tasks: Task[];
};

// Multi-card geometry per design: card width = screen - 24 - 32, 12px
// between cards, and the previous/next cards peek 16px on each side.
// A single card has no neighbour to preview, so it uses the same 16px
// horizontal inset as the group cards below it.
const MULTI_CARD_WIDTH_INSET = 24 + 32;
const SINGLE_CARD_WIDTH_INSET = theme.spacing.m * 2;
const CARD_GAP = 12;
const AVATAR_SIZE = theme.spacing.l;
// Hairline white ring separating overlapped avatars. Sub-0.5px borders may
// render unevenly on some Android densities; bump if QA reports gaps.
const AVATAR_BORDER_WIDTH = 0.3;
// At most 3 slots: up to 3 people show everyone; 4+ show 2 avatars plus a
// "+N" chip in the third slot (4 people → "+2", 5 → "+3").
const MAX_AVATAR_SLOTS = 3;

export const getHeroCarouselGeometry = (
  windowWidth: number,
  taskCount: number
) => {
  const cardWidth =
    windowWidth -
    (taskCount === 1 ? SINGLE_CARD_WIDTH_INSET : MULTI_CARD_WIDTH_INSET);
  return {
    cardWidth,
    edgeInset: (windowWidth - cardWidth) / 2,
    itemGap: taskCount > 1 ? CARD_GAP : 0,
  };
};

const HeroAvatars = ({ task }: { task: Task }) => {
  const users = (task.members ?? [])
    .map((m) => m.user)
    .filter((user): user is NonNullable<typeof user> => user != null);
  if (users.length === 0) return null;

  const visible =
    users.length <= MAX_AVATAR_SLOTS
      ? users
      : users.slice(0, MAX_AVATAR_SLOTS - 1);
  const overflow = users.length - visible.length;

  return (
    <Box flexDirection='row' alignItems='center'>
      {visible.map((user, index) => (
        <Box
          key={user.id}
          style={{
            marginLeft: index === 0 ? 0 : -theme.spacing.xs,
            borderRadius: (AVATAR_SIZE + AVATAR_BORDER_WIDTH * 2) / 2,
            borderWidth: AVATAR_BORDER_WIDTH,
          }}
          borderColor='white'>
          <Avatar
            avatar={user.avatar}
            label={user.name}
            labelVariant='labelHeroCarousel'
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
          />
        </Box>
      ))}
      {overflow > 0 && (
        <Box
          style={{
            marginLeft: -theme.spacing.xs,
            borderWidth: AVATAR_BORDER_WIDTH,
          }}
          width={AVATAR_SIZE + AVATAR_BORDER_WIDTH * 2}
          height={AVATAR_SIZE + AVATAR_BORDER_WIDTH * 2}
          borderRadius='m'
          borderColor='white'
          backgroundColor='textPrimary'
          alignItems='center'
          justifyContent='center'>
          <Text variant='labelHeroCarousel' color='white'>
            +{overflow}
          </Text>
        </Box>
      )}
    </Box>
  );
};

const DOT_ANIMATION_DURATION = 150;

const HeroCarouselDot = ({ active }: { active: boolean }) => {
  const animatedStyle = useAnimatedStyle(() => ({
    width: withTiming(active ? 16 : 5, { duration: DOT_ANIMATION_DURATION }),
    backgroundColor: withTiming(
      active ? theme.colors.grey05 : theme.colors.grey03,
      { duration: DOT_ANIMATION_DURATION }
    ),
  }));

  return (
    <Animated.View
      style={[
        {
          height: 5,
          borderRadius: theme.borderRadii.xxxs,
          marginHorizontal: theme.spacing.xxxs,
        },
        animatedStyle,
      ]}
    />
  );
};

/**
 * Overdue hero carousel (TA-10186 AC12/AC13): max 5 swipeable cards, sorted
 * by soonest overdue date, background colors rotating through 3 mild tones.
 * The carousel loops — swiping left on the first card reveals the last one —
 * implemented by tripling the data and silently re-centering onto the middle
 * copy whenever momentum ends on an outer copy. The caller is responsible
 * for capping/sorting via selectHeroTasks and the scroll-fade wrapper (AC14).
 */
export const HeroCarousel: React.FC<HeroCarouselProps> = ({ tasks }) => {
  const { t: f } = useTranslation('format');
  const { navigateToTask } = useAppNavigation();
  const { width: windowWidth } = useWindowDimensions();
  const listRef = useRef<FlatList<Task>>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const baseCount = tasks.length;
  const { cardWidth, edgeInset, itemGap } = getHeroCarouselGeometry(
    windowWidth,
    baseCount
  );
  const snapInterval = cardWidth + itemGap;

  const loop = baseCount > 1;
  const data = useMemo(
    () => (loop ? [...tasks, ...tasks, ...tasks] : tasks),
    [loop, tasks]
  );

  const handleMomentumEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offset = e.nativeEvent.contentOffset.x;
      const index = Math.round(offset / snapInterval);
      setActiveIndex(((index % baseCount) + baseCount) % baseCount);
      if (!loop) return;
      // Landed on an outer copy — jump to the equivalent card in the middle
      // copy without animation so the next swipe can continue either way.
      if (index < baseCount || index >= baseCount * 2) {
        const recentered =
          index < baseCount ? index + baseCount : index - baseCount;
        listRef.current?.scrollToOffset({
          offset: recentered * snapInterval,
          animated: false,
        });
      }
    },
    [baseCount, loop, snapInterval]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: Task; index: number }) => {
      const dueDate = item.dueDate
        ? f('monthAndDayYearV3', { val: localDateFromUTCDate(item.dueDate) })
        : '';
      // Color follows the card's position in the base list so each task keeps
      // its color across loop copies (slides 1&4 / 2&5 share colors, AC12).
      const baseIndex = index % baseCount;
      return (
        <TouchableOpacity
          testID={`hero-card-${item.id}`}
          accessibilityLabel={`Overdue task ${item.name}`}
          onPress={() => navigateToTask(item)}>
          <Box
            width={cardWidth}
            style={{ marginRight: itemGap }}
            padding='m'
            borderRadius='xs'
            backgroundColor={heroColorForIndex(baseIndex)}>
            <Text
              variant='labelEmphasizedChatDetialTitle'
              color='textPrimary'
              numberOfLines={1}>
              {item.name}
            </Text>
            <Text
              marginTop='xs'
              variant='webBodyHintSubline'
              color='textSecondary'
              numberOfLines={1}>
              {item.project?.name ?? 'Unknown Project'}
            </Text>
            <Box
              marginTop='m'
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'>
              <Box flexDirection='row' alignItems='center'>
                <Icon name='Clock' variant='s' color='alertRed' />
                <Text
                  marginLeft='xxs'
                  variant='bodySecondaryNew'
                  color='alertRed'>
                  Overdue{dueDate ? ` · ${dueDate}` : ''}
                </Text>
              </Box>
              <HeroAvatars task={item} />
            </Box>
          </Box>
        </TouchableOpacity>
      );
    },
    [baseCount, cardWidth, f, itemGap, navigateToTask]
  );

  if (baseCount === 0) return null;

  return (
    <Box paddingTop='xs' paddingBottom='s'>
      <FlatList
        ref={listRef}
        data={data}
        horizontal
        keyExtractor={(item, index) => `${item.id}-${index}`}
        renderItem={renderItem}
        getItemLayout={(_, index) => ({
          length: snapInterval,
          offset: snapInterval * index,
          index,
        })}
        initialScrollIndex={loop ? baseCount : 0}
        showsHorizontalScrollIndicator={false}
        // With one card the content exactly fills the viewport. iOS can still
        // provide the expected elastic left/right drag, but there is no real
        // scroll offset (including the old trailing 12px gap) to settle on.
        alwaysBounceHorizontal={baseCount === 1}
        snapToInterval={snapInterval}
        snapToAlignment='start'
        decelerationRate='fast'
        contentContainerStyle={{ paddingHorizontal: edgeInset }}
        onMomentumScrollEnd={handleMomentumEnd}
      />
      {baseCount > 1 && (
        <Box
          flexDirection='row'
          justifyContent='center'
          alignItems='center'
          marginTop='xs'>
          {tasks.map((task, index) => (
            <HeroCarouselDot key={task.id} active={index === activeIndex} />
          ))}
        </Box>
      )}
    </Box>
  );
};
